import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useBrandName } from "../../context/SettingsContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { ADMIN_FIELD, BTN_PRIMARY } from "../../config/brand";
import { toast } from "sonner";

export default function AdminLogin() {
  const { login, isAuthenticated, loading } = useAuth();
  const brandName = useBrandName();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center text-slate-500">
        <Loader2 className="h-8 w-8 animate-spin text-[#3a00e0] mb-3" />
        <p className="text-sm">Vérification de la session...</p>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Email et mot de passe requis");
      return;
    }
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Connexion réussie");
    } catch (error) {
      toast.error(error.message || "Identifiants invalides");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white border-slate-200 text-slate-900 relative shadow-sm">
        <CardHeader className="text-center pb-2">
          <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[rgba(58,0,224,0.07)] flex items-center justify-center">
            <Lock className="h-6 w-6 text-[#3a00e0]" />
          </div>
          <CardTitle className="text-2xl text-slate-900 tracking-tight">{brandName} Admin</CardTitle>
          <span className="section-title-rule section-title-rule--center mx-auto" aria-hidden="true" />
          <CardDescription className="text-slate-500">
            Connectez-vous pour accéder au panel d&apos;administration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@digirobot.fr"
                required
                disabled={submitting}
                className={ADMIN_FIELD}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={submitting}
                className={ADMIN_FIELD}
              />
            </div>
            <Button
              type="submit"
              className={`w-full mt-2 ${BTN_PRIMARY}`}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Connexion...
                </>
              ) : (
                "Se connecter"
              )}
            </Button>
            <p className="text-center pt-1">
              <Link to="/" className="text-sm text-[#3a00e0] hover:underline underline-offset-2">
                Retour au site
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
